import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { useLocation, useNavigate } from 'react-router-dom';

import { MessageCenterNotificationsCount } from 'src/features/message-center';
import { getRoute } from 'src/routes';
import { AccrualIcon, AppIcon } from 'src/shared/components';
import { CalendarIcon, DescriptionIcon, ForumIcon, HomeIcon, SupportIcon } from 'src/shared/components/icons';
import { FeaturesEnums } from 'src/shared/enums';
import { ComponentWithConditionalWrapper, ComponentWithFeatures } from 'src/shared/hocs';
import { useTranslate } from 'src/shared/providers';

import './NavigationMenu.styles.scss';

export const NavigationMenu: FC = () => {
  const { t } = useTranslate();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [currentActiveRoute, setCurrentActiveRoute] = useState<string>(location.pathname);

  const menu = useMemo(
    () => [
      {
        name: t('menu.home'),
        icon: <HomeIcon />,
        route: getRoute('root'),
        id: 'home-menu-item',
      },
      {
        name: t('timesheets.timesheet'),
        icon: <DescriptionIcon />,
        route: getRoute('timesheets'),
        id: 'timesheets-menu-item',
        feature: FeaturesEnums.Timesheet,
      },
      {
        name: t('menu.calendar'),
        icon: <CalendarIcon />,
        route: getRoute('calendar'),
        id: 'calendar-menu-item',
        feature: FeaturesEnums.EmployeeCalendar,
      },
      {
        name: t('menu.messageCenter'),
        icon: <ForumIcon />,
        route: getRoute('messageCenter'),
        id: 'message-center-menu-item',
        feature: FeaturesEnums.Notifications,
        extra: <MessageCenterNotificationsCount />,
      },
      {
        name: t('menu.balances'),
        icon: <AccrualIcon />,
        route: getRoute('balances'),
        id: 'balances-menu-item',
        feature: [FeaturesEnums.AccrualSummary, FeaturesEnums.AccrualsFunction],
        strictFeature: true,
      },
      {
        name: t('menu.settings'),
        icon: <SupportIcon />,
        route: getRoute('settings'),
        id: 'settings-menu-item',
      },
    ],
    [],
  );

  useEffect(() => {
    if (currentActiveRoute === location.pathname) {
      return;
    }

    setCurrentActiveRoute(location.pathname);
  }, [location.pathname]);

  const handleNavigation = (route: string) => {
    setCurrentActiveRoute(route);
    navigate(route);
  };

  return (
    <Paper square className="d-flex flex-column align-items-center m-0 navigation-container">
      <div className="navigation-content text-center">
        <AppIcon theme={theme} className="site-icon" />
        {menu.map(({ icon, name, route, id, feature, strictFeature, extra }, index) => {
          const activeStyle = currentActiveRoute === route ? 'active' : '';

          return (
            <ComponentWithConditionalWrapper key={`app-menu-item-${index}`} shouldWrap={!!feature}>
              <ComponentWithFeatures features={feature} strict={strictFeature}>
                <Box
                  id={id}
                  className={`navigation-item ${activeStyle} ${theme.palette.mode}`}
                  onClick={() => handleNavigation(route)}
                  sx={{ position: 'relative' }}
                >
                  <div className="text-center d-block">{icon}</div>
                  <label className="d-block mt-2">{name}</label>
                  {extra}
                </Box>
              </ComponentWithFeatures>
            </ComponentWithConditionalWrapper>
          );
        })}
      </div>
    </Paper>
  );
};
